import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../integrations/supabase/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setEmailSent(true);
      toast.success('Password reset email sent. Please check your inbox.');
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email.');
      console.error('Reset password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF7F0] px-4 py-12">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              className="mr-2 p-0 h-8 w-8" 
              onClick={() => navigate('/auth')}
            >
              <ArrowLeft className="h-5 w-5 text-brand-secondary" />
            </Button>
            <CardTitle className="text-2xl font-bold text-brand-secondary">Reset Password</CardTitle>
          </div>
          <CardDescription>
            {!emailSent 
              ? 'Enter your email address and we will send you a link to reset your password.'
              : 'Check your email for a link to reset your password.'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={error ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500">
                        We'll send a password reset link to this email.
                </p>
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                  )}
              </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#967F81] hover:bg-[#E33D16] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
          ) : (
            <div className="text-center py-4">
              <p className="mb-4 text-brand-secondary/80">
                If an account exists with this email, you'll receive a password reset link shortly.
              </p>
              <Button 
                variant="outline" 
                className="mt-2"
                onClick={() => navigate('/auth')}
              >
                Return to Login
              </Button>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-sm text-brand-secondary/60 text-center w-full">
            Remember your password? <Link to="/auth" className="text-brand-primary hover:underline">Sign in</Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPassword; 